/**
 * Calculates y-axis parameters for creating tidy chart axes.
 * "Tidy" means tick values are in increments of 1, 2, or 5, scaled by powers of 10.
 *
 * @param yValues - Array of y-values from the data
 * @param ySpan - Optional fixed span for the y-axis. If provided, the axis will be centered on the median with this span.
 * @returns Object containing yMin, yMax, tickAmount, and tickIncrement
 */
export function calculateYAxisParams(
  yValues: number[],
  ySpan: number | string | null = null
): {
  yMin: number;
  yMax: number;
  tickAmount: number;
  tickIncrement: number;
} {
  if (yValues.length === 0) {
    return { yMin: 0, yMax: 1, tickAmount: 5, tickIncrement: 0.2 };
  }

  const yMinRaw = Math.min(...yValues);
  const yMaxRaw = Math.max(...yValues);

  // Calculate span
  let finalYSpan: number;
  let yMidpointRaw: number;
  if ((ySpan === null) || (typeof ySpan === 'string')){
    const ySpanRaw = 1.5 * (yMaxRaw - yMinRaw);

    // Calculate magnitude for rounding
    const magnitudeRaw = Math.floor(Math.log10(ySpanRaw)) - 1;

    // Round ySpanRaw to nearest (magScale * 2)
    const roundingIncrement = Math.pow(10, magnitudeRaw);
    finalYSpan = Math.round(ySpanRaw / roundingIncrement) * roundingIncrement;
    yMidpointRaw = yMinRaw + (yMaxRaw - yMinRaw) / 2;
  }
  else{
      finalYSpan = ySpan;
          // Use median of values
      const sortedValues = [...yValues].sort((a, b) => a - b);
      const medianIndex = Math.floor(sortedValues.length / 2);
      yMidpointRaw =
          sortedValues.length % 2 === 0
            ? (sortedValues[medianIndex - 1] + sortedValues[medianIndex]) / 2
            : sortedValues[medianIndex];
  }

  // Round midpoint to nearest (magScale * 2)
  const magnitude = Math.floor(Math.log10(finalYSpan)) - 1;
  const magScale = Math.pow(10, magnitude);
  const midpointRoundingIncrement = magScale * 5;
  const yMidpoint = Math.round(yMidpointRaw / midpointRoundingIncrement) * midpointRoundingIncrement;

  // console.log(`yMidpoint = ${yMidpoint}, yMidpointRaw = ${yMidpointRaw}, magScale ${magScale}, finalYSpan ${finalYSpan}`)

  // Calculate final min and max
  const yMin = yMidpoint - finalYSpan / 2;
  const yMax = yMidpoint + finalYSpan / 2;

  // Calculate tidy tick increment
  const tickIncrement = calculateTidyTickIncrement(finalYSpan);

  // Calculate tick amount (number of ticks including endpoints)
  const tickAmount = Math.round(finalYSpan / tickIncrement);

  // logYAxisParams(yMin, yMax, tickAmount, tickIncrement)

  return {
    yMin,
    yMax,
    tickAmount,
    tickIncrement,
  };
}

/**
 * Calculates a "tidy" tick increment that results in 5-12 ticks.
 * The increment is based on the magnitude (from log10) and uses multipliers of 1, 2, or 5.
 * Prefers the lowest tickAmount (fewest ticks) within the 5-12 range.
 */
function calculateTidyTickIncrement(span: number): number {
  // Calculate the valid range of increments for 5-12 ticks
  const maxIncrement = span / 5; // Gives minimum 5 ticks
  const minIncrement = span / 12; // Gives maximum 12 ticks

  // Determine the range of magnitudes to search
  const maxMagnitude = Math.floor(Math.log10(maxIncrement));
  const minMagnitude = Math.floor(Math.log10(minIncrement));

  const baseMultipliers = [1, 2, 5];
  let bestIncrement: number | null = null;
  let bestTickCount = Infinity;

  // Search through magnitudes from largest to smallest (for fewer ticks)
  for (let magnitude = maxMagnitude; magnitude >= minMagnitude - 1; magnitude--) {
    const scale = Math.pow(10, magnitude);

    for (const multiplier of baseMultipliers) {
      const tickIncrement = multiplier * scale;
      const tickAmount = span / tickIncrement;

      // Check if this gives us a valid number of ticks (5-12)
      if (tickAmount >= 5 && tickAmount <= 12) {
        // Keep the increment with the lowest tick count (fewest ticks)
        if (bestIncrement === null || tickAmount < bestTickCount) {
          bestIncrement = tickIncrement;
          bestTickCount = tickAmount;
        }
      }
    }
  }

  // If we found a valid increment, use it; otherwise fallback
  return bestIncrement !== null ? bestIncrement : span / 8;
}

/**
 * Helper function to log y-axis parameters to the console for debugging.
 * Calculates and logs the result of calculateYAxisParams.
 *
 * @param yValues - Array of y-values from the data
 * @param ySpan - Optional fixed span for the y-axis
 * @param label - Optional label to identify the log output
 */
export function logYAxisParams(
  yMin: number,
  yMax: number,
  tickAmount: number,
  tickIncrement: number,
): void {
  // eslint-disable-next-line no-console
  console.log('logYAxisParams:', { yMin, yMax, tickAmount, tickIncrement });
}

